import { NgIf, NgFor, AsyncPipe } from '@angular/common';
import { Component, OnInit } from '@angular/core';

import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';

import { MatButton } from '@angular/material/button';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSelectChange, MatSelect, MatOption } from '@angular/material/select';
import { RootState } from '@next/core-lib/app';
import { TranslatePipe } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, skipWhile, take, withLatestFrom } from 'rxjs/operators';

import { Barn } from '../../../settings/models/settings.model';
import { getBarns } from '../../../settings/store/selectors/settings.selectors';

import Utils from '../../../utils';
import { AnimalGroup, ProductionType, SupplySystemType } from '../../models/animal-group.model';
import * as animalGroupActions from '../../store/animal-group.actions';
import { getSelectedAnimalGroup } from '../../store/animal-group.selectors';

@Component({
  selector: 'app-general',
  templateUrl: './general.component.html',
  imports: [
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgIf,
    MatError,
    MatSelect,
    NgFor,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    MatButton,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class GeneralComponent implements OnInit {
  animalGroup$: Observable<AnimalGroup> = this.store.select(getSelectedAnimalGroup);
  routeUrl$: Observable<string> = this.store.select(RouterSelectors.getPath);

  barns$: Observable<Barn[]> = this.store
    .select(getBarns)
    .pipe(
      map((barns) =>
        barns.filter(
          (b) => !!b.properties && Object.prototype.hasOwnProperty.call(b.properties, 'sections'),
        ),
      ),
    );

  productionTypes = Object.values(ProductionType);
  systemTypes = Object.values(SupplySystemType);
  selectedBarn$: BehaviorSubject<Barn> = new BehaviorSubject(<Barn>{});

  form: FormGroup = new FormGroup({
    name: new FormControl(null, Validators.required),
    description: new FormControl('', Validators.required),
    productionType: new FormControl('', Validators.required),
    startDate: new FormControl('', Validators.required),
    endDate: new FormControl(''),
    supplySystemType: new FormControl('', Validators.required),
    barn: new FormControl('', Validators.required),
    sectionNames: new FormControl({ value: [], disabled: true }),
  });

  constructor(private readonly store: Store<RootState>) {}

  ngOnInit(): void {
    this.form.controls.barn.valueChanges.subscribe((b: Barn) => {
      if (b.properties.sections) {
        this.form.controls.sectionNames.enable();
      }
    });
    this.routeUrl$.subscribe((url) => {
      if (!url.endsWith('/animal-group/add')) {
        this.animalGroup$
          .pipe(
            withLatestFrom(this.barns$),
            skipWhile(
              ([animalGroup, barns]) =>
                animalGroup === null || animalGroup === undefined || !barns || barns.length === 0,
            ),
            take(1),
          )
          .subscribe(([animalGroup, barns]: [AnimalGroup, Barn[]]) => {
            this.form.controls.name.setValue(animalGroup.name);
            this.form.controls.description.setValue(animalGroup.description);
            this.form.controls.productionType.setValue(animalGroup.productionType);
            this.form.controls.startDate.setValue(animalGroup.startDate);
            this.form.controls.endDate.setValue(animalGroup.endDate ? animalGroup.endDate : '');
            this.form.controls.supplySystemType.setValue(animalGroup.supplySystemType);
            const selectedBarn = barns.find((b) => b.id === animalGroup.barn.id);
            if (selectedBarn) {
              this.selectedBarn$.next(selectedBarn);
              this.form.controls.barn.setValue(barns.find((b) => b.id === animalGroup.barn.id));
              this.form.controls.sectionNames.setValue(
                animalGroup.sectionNames.map((selectedSection) =>
                  selectedBarn.properties?.sections?.find((s) => s === selectedSection),
                ),
              );
            }
          });
      }
    });
  }

  setSelectedBarn(selection: MatSelectChange) {
    this.selectedBarn$.next(<Barn>selection.value);
  }

  areBarnsEqual(p1: Barn, p2: Barn): boolean {
    if (!p1 || !p2) {
      return false;
    }
    return p1.id === p2.id && p1.name === p2.name;
  }

  private getAnimalGroup(animalGroup: AnimalGroup) {
    return {
      animalGroup: {
        ...animalGroup,
        ...this.form.value,
        id: animalGroup ? animalGroup.id : undefined,
        startDate:
          typeof (<unknown>this.form.controls.startDate.value) !== 'string'
            ? Utils.toUtcDate(<Date>this.form.controls.startDate.value).toISOString()
            : <string>this.form.controls.startDate.value,
        endDate:
          typeof (<unknown>this.form.controls.endDate.value) !== 'string'
            ? Utils.toUtcDate(<Date>this.form.controls.endDate.value).toISOString()
            : <string>this.form.controls.endDate.value,
        observation: animalGroup ? animalGroup.observation : {},
        feedAdviceApplications: animalGroup ? animalGroup.feedAdviceApplications : [],
      } as AnimalGroup,
    };
  }

  save() {
    if (this.form.valid) {
      this.animalGroup$.pipe(take(1)).subscribe((animalGroup) => {
        if (animalGroup) {
          this.store.dispatch(
            animalGroupActions.upsertAnimalGroupGeneral(this.getAnimalGroup(animalGroup)),
          );
        } else {
          this.store.dispatch(
            animalGroupActions.createAnimalGroup(this.getAnimalGroup(animalGroup)),
          );
        }
      });
    }
  }
}
